import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Building2,
  Landmark,
  MessageCircle,
  FileText,
  Key,
  Eye,
  CalendarDays,
  Rocket,
  User,
  CreditCard,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useLandlordBadges from '../../hooks/useLandlordBadges';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/landlord', icon: LayoutGrid, exact: true, tutorial: 'landlord-dashboard' },
  { label: 'Panden', path: '/landlord/properties', icon: Building2, tutorial: 'landlord-panden' },
  { label: 'Berichten', path: '/landlord/messages', icon: MessageCircle, tutorial: 'landlord-berichten' },
  { label: 'Contracten', path: '/landlord/contracts', icon: FileText, tutorial: 'landlord-contracten' },
  { label: 'Verhuurde Panden', path: '/landlord/verhuurde-panden', icon: Key, tutorial: 'landlord-verhuurde-panden' },
  { label: 'Bezichtigingen', path: '/landlord/viewings', icon: Eye, tutorial: 'landlord-bezichtigingen' },
  { label: 'Agenda', path: '/landlord/agenda', icon: CalendarDays, tutorial: 'landlord-agenda' },
  { label: 'Mijn Immo', path: '/landlord/mijn-immo', icon: Landmark, tutorial: 'landlord-mijn-immo', comingSoon: true },
  { label: 'CRM Koppeling', path: '/landlord/crm', icon: Rocket, tutorial: 'landlord-crm' },
  { label: 'Profiel', path: '/landlord/profile', icon: User, tutorial: 'landlord-profiel' },
  { label: 'Abonnement', path: '/landlord/billing', icon: CreditCard, tutorial: 'landlord-abonnement' }
];

export default function LandlordPrivateShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { getBadgeCount } = useLandlordBadges(user?.uid);

  // Persist collapsed state in localStorage
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('landlordSidebarCollapsed') === '1';
    } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('landlordSidebarCollapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const sidebarWidth = collapsed ? 68 : 256;

  return (
    <div className="min-h-screen bg-[#F6FAFF] relative overflow-hidden">
      {/* Fixed sidebar — pinned to the left edge */}
      <aside
        className="hidden md:flex fixed left-0 top-0 bottom-0 z-30 flex-col bg-white/95 backdrop-blur-md border-r border-slate-200/60 shadow-sm transition-all duration-300 ease-in-out"
        style={{ width: sidebarWidth, paddingTop: '4.5rem' }}
      >
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {/* Toggle button */}
          <button
            onClick={toggleCollapsed}
            className="w-full flex items-center justify-center p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors mb-1"
            title={collapsed ? 'Menu uitklappen' : 'Menu inklappen'}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>

          {/* Nav items */}
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const badge = getBadgeCount(item.path);
            return (
              <div key={item.path} className="relative group" data-tutorial={item.tutorial}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full text-left flex items-center rounded-xl text-sm transition-all duration-200 ${
                    collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2.5'
                  } ${
                    active
                      ? 'bg-[#5EABEB]/10 text-[#2B6FA6] font-medium border-l-2 border-[#5EABEB]'
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-[#2B6FA6]' : item.comingSoon ? 'text-slate-300' : 'text-slate-400'}`} />
                  {!collapsed && <span className={`flex-1 truncate ${item.comingSoon ? 'text-slate-400' : ''}`}>{item.label}</span>}
                  {!collapsed && item.comingSoon && (
                    <span className="text-[9px] font-bold text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-full">Soon</span>
                  )}
                  {badge > 0 && !item.comingSoon && (
                    <span className={`bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${
                      collapsed ? 'absolute -top-1 -right-1' : ''
                    }`}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </button>
                {/* Tooltip on hover when collapsed */}
                {collapsed && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 shadow-lg">
                    {item.label}
                    {badge > 0 && (
                      <span className="ml-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] inline-flex items-center justify-center px-1">
                        {badge > 9 ? '9+' : badge}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Mobile nav bar (horizontal scroll) */}
      <div className="md:hidden overflow-x-auto px-4 pt-4">
        <div className="bg-white/80 backdrop-blur-sm ring-1 ring-slate-200/60 shadow-sm p-2 rounded-2xl flex gap-1 min-w-max">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const badge = getBadgeCount(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                  active
                    ? 'bg-[#5EABEB]/10 text-[#2B6FA6] border border-[#5EABEB]/25'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className={`w-4 h-4 ${active ? 'text-[#2B6FA6]' : 'text-slate-400'}`} />
                <span>{item.label}</span>
                {badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content — offset by sidebar width on desktop */}
      <div
        className="hidden md:block transition-all duration-300 ease-in-out shell-content overflow-hidden"
        style={{ marginLeft: sidebarWidth }}
      >
        <main className="relative z-10 min-h-screen">
          {children}
        </main>
      </div>

      {/* Mobile content — no offset */}
      <div className="md:hidden shell-content overflow-hidden">
        <main className="relative z-10 min-h-screen">
          {children}
        </main>
      </div>

      <style>{`
        .shell-content .container-professional {
          max-width: none;
        }
      `}</style>
    </div>
  );
}
